import { useEffect, useRef, useState } from 'react';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { Tooltip } from '../../common/Tooltip';
import { downloadFile } from '../../materials/shared/downloadUtils';
import { MaterialPreviewModal } from '../../materials/shared/MaterialPreviewModal';
import { fetchTemporaryMaterialById } from '../../../features/materials/api';
import { useToast } from '../../../hooks/useToast';
import type { TemporaryMaterial } from '../../../features/materials/materialsSlice';

/**
 * ストーリーボードタブ選択中: 素材グリッドの代わりに絵コンテ画像を表示する。
 * 表示状態 (storyboardImageUrl 等) は materialsPanelStore から直接読む。
 */
export function StoryboardTabPane() {
  const storyboardImageUrl = useMaterialsPanelStore((state) => state.storyboardImageUrl);
  const storyboardLabel = useMaterialsPanelStore((state) => state.storyboardLabel);
  const storyboardScenarioTitle = useMaterialsPanelStore((state) => state.storyboardScenarioTitle);
  const storyboardItems = useMaterialsPanelStore((state) => state.storyboardItems);
  const closeStoryboardPreview = useMaterialsPanelStore((state) => state.closeStoryboardPreview);
  const selectStoryboardByOffset = useMaterialsPanelStore((state) => state.selectStoryboardByOffset);

  // ストーリーボードが切り替わったら必ず画像の一番上から表示する。
  // タブ切替や送り/戻すで storyboardImageUrl が差し替わってもスクロール領域の
  // 位置は引き継がれるため、URL が変わるたびに明示的に先頭へ戻す。
  const imageScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    imageScrollRef.current?.scrollTo({ top: 0 });
  }, [storyboardImageUrl]);

  // 送り/戻すボタンの活性条件: 一覧内に現在画像があり、両端でなければ
  const storyboardCurrentIdx = storyboardItems.findIndex((it) => it.url === storyboardImageUrl);
  const canStoryboardPrev = storyboardCurrentIdx > 0;
  const canStoryboardNext = storyboardCurrentIdx >= 0 && storyboardCurrentIdx < storyboardItems.length - 1;

  // 表示中の絵コンテの素材ID。
  // ⚠️ storyboardItems[].id は scenario_storyboards の行 ID であって素材 ID ではない。
  //    素材詳細モーダルの取得には materialId (素材 ID) を使う。
  const currentStoryboardMaterialId = storyboardItems[storyboardCurrentIdx]?.materialId ?? null;

  // 素材詳細モーダル: 素材一覧 (sortedMaterials/タブ) に依存せず、ID で直接取得して開く。
  // 絵コンテ素材は temporary なので isTemporary で表示する。
  const toast = useToast();
  const [detailMaterial, setDetailMaterial] = useState<TemporaryMaterial | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const openMaterialDetail = async () => {
    if (!currentStoryboardMaterialId || isLoadingDetail) return;
    setIsLoadingDetail(true);
    try {
      const material = await fetchTemporaryMaterialById(currentStoryboardMaterialId);
      if (material) {
        setDetailMaterial(material);
      } else {
        toast.error('素材が見つかりませんでした');
      }
    } catch {
      toast.error('素材情報の取得に失敗しました');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  return (
    <div className="storyboard-tab-pane">
      {/* 上段: 左=ラベル+送り/戻すナビ、右=ダウンロード/閉じる */}
      <div className="storyboard-tab-toolbar">
        <div className="storyboard-tab-label-nav">
          <button
            type="button"
            className="storyboard-tab-nav-button"
            onClick={() => selectStoryboardByOffset(-1)}
            disabled={!canStoryboardPrev}
            aria-label="前のストーリーボード"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="storyboard-tab-label-text">{storyboardLabel ?? ''}</span>
          <button
            type="button"
            className="storyboard-tab-nav-button"
            onClick={() => selectStoryboardByOffset(1)}
            disabled={!canStoryboardNext}
            aria-label="次のストーリーボード"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
        <div className="storyboard-tab-actions">
          <button
            type="button"
            className="storyboard-tab-icon-button"
            onClick={openMaterialDetail}
            disabled={!currentStoryboardMaterialId || isLoadingDetail}
            aria-label="素材の詳細を開く"
          >
            <Tooltip content="素材の詳細を開く">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="11" x2="12" y2="16" />
                <circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" />
              </svg>
            </Tooltip>
          </button>
          <button
            type="button"
            className="storyboard-tab-icon-button"
            onClick={async () => {
              if (!storyboardImageUrl) return;
              // ファイル名は {シナリオ名}_{A,B,...}.png。レターは表示中候補の生ラベル
              // (storyboardItems[].label) から取得し、UI のレターバッジと一致させる。
              const letter = storyboardItems[storyboardCurrentIdx]?.label || '';
              const safeTitle =
                (storyboardScenarioTitle || '').replace(/[\\/:*?"<>|]/g, '').trim() || 'storyboard';
              const filename = letter ? `${safeTitle}_${letter}.png` : `${safeTitle}.png`;
              try { await downloadFile(storyboardImageUrl, filename); } catch { /* noop */ }
            }}
            aria-label="ストーリーボード画像をダウンロード"
          >
            <Tooltip content="ダウンロード">
              <img src="/download_bottom.svg" alt="" style={{ width: 18, height: 18 }} />
            </Tooltip>
          </button>
          <button
            type="button"
            className="storyboard-tab-icon-button"
            onClick={() => closeStoryboardPreview()}
            aria-label="ストーリーボードタブを閉じる"
          >
            <Tooltip content="ストーリーボードタブを閉じる">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Tooltip>
          </button>
        </div>
      </div>
      {storyboardImageUrl && (
        <div className="storyboard-tab-image-scroll" ref={imageScrollRef}>
          <img
            src={storyboardImageUrl}
            alt="ストーリーボード拡大表示"
            className="storyboard-tab-image"
          />
        </div>
      )}

      {detailMaterial && (
        <MaterialPreviewModal
          material={detailMaterial}
          materialId={detailMaterial.id}
          isTemporary
          showEditControls
          showShareButton={false}
          onClose={() => setDetailMaterial(null)}
          onRequestRefresh={async () => {
            try {
              const refreshed = await fetchTemporaryMaterialById(detailMaterial.id);
              if (refreshed) setDetailMaterial(refreshed);
            } catch {
              /* noop: 取得失敗時は表示中の内容を維持 */
            }
          }}
        />
      )}
    </div>
  );
}
